import { z } from 'zod';
import { isExcludedFolderName } from './folder-tree.js';
/**
 * Contrats des ÉCRITURES SUR LES DOSSIERS (XPHOTO-32, lots 4 et 5) : créer, renommer,
 * déplacer, SUPPRIMER. Source unique de vérité front + back.
 *
 * ===========================================================================
 * POURQUOI CES OPÉRATIONS SONT SYNCHRONES (et pas des jobs)
 * ===========================================================================
 * Tout ce que fait un renommage de dossier, c'est UN `rename()` (atomique sur le même
 * volume) suivi de trois `UPDATE` de PRÉFIXE sur des colonnes indexées. C'est
 * instantané, quel que soit le nombre de photos concernées — rien à paralléliser, rien
 * à reprendre, aucune progression à afficher. Le passer par le moteur de jobs
 * n'apporterait qu'une latence et une cloche qui clignote ; l'appelant n'aurait même
 * pas le droit de recharger sa vue avant la fin. Les jobs restent pour ce qui itère
 * fichier par fichier (`move`, `compress`, `exif_*`).
 *
 * ===========================================================================
 * LE CŒUR DU SUJET : QUATRE CHOSES RÉFÉRENCENT UN DOSSIER PAR SON CHEMIN
 * ===========================================================================
 * Un dossier n'est pas un objet d'xphoto (cf. `ADR-0013`) : son identité EST son
 * chemin. Le renommer change donc un PRÉFIXE partout où ce chemin est stocké.
 * Inventaire EXHAUSTIF du schéma, et ce qu'on en fait :
 *
 *  1. `assets.rel_path` + `asset_file_paths.rel_path` → **PROPAGÉS**. Sans quoi la base
 *     décrit un disque qui n'existe plus : galerie peuplée, originaux en 404, et aucune
 *     auto-réparation avant un re-scan complet. (`assets.dir_path` est une colonne
 *     GÉNÉRÉE depuis `rel_path`, migration `0043` : elle suit toute seule et REFUSE
 *     d'être écrite.)
 *  2. `shares.folder_path` (`kind = 'folder'`, migration `0044`) → **PROPAGÉ**. Un lien
 *     public `/p/:token` déjà envoyé à quelqu'un doit survivre au rangement : le
 *     destinataire n'a aucun moyen de savoir que la photo a changé de dossier, et un
 *     lien qui meurt en silence est pire qu'un lien révoqué.
 *  3. `access_grants.subtree_path` (`0001_init`) → **PROPAGÉ**. Sans quoi un bénéficiaire
 *     perd l'accès à sa branche sans que personne ne s'en aperçoive — la panne la plus
 *     silencieuse de toutes.
 *  4. Prédicat `folder:` des FILTRES ENREGISTRÉS (`filter-expr.ts`, AST stocké en
 *     TEXTE dans `saved_filters.expr`) → **PAS RÉÉCRIT**, décision explicite de
 *     l'utilisateur. Réécrire du texte d'un mini-langage (guillemets, parenthèses,
 *     opérateurs, casse conservée) pour y substituer un chemin, c'est réimplémenter un
 *     refactoring d'AST sur une expression que l'utilisateur a écrite À LA MAIN — le
 *     round-trip `parse`/`toText` la re-canoniserait au passage, donc la modifierait
 *     sous ses yeux sans qu'il l'ait demandé. À la place, l'UI AVERTIT, en comptant les
 *     filtres concernés (`folderImpactSchema.savedFilters`) : l'utilisateur décide.
 *
 * ===========================================================================
 * LOT 5 — SUPPRIMER UN DOSSIER : « celui-ci », pas « tous ceux qui traînent »
 * ===========================================================================
 * La suppression CIBLÉE d'un sous-arbre ne remplace PAS l'outil de balayage global
 * « Nettoyer les dossiers vides » (`empty-folders.ts`, ADR-0010) : l'un répond à
 * « supprime CE dossier », l'autre à « fais le ménage partout ». Ils partagent en
 * revanche la garantie de sûreté, et c'est volontaire — `rmdir` seul, plus un `unlink`
 * BORNÉ à la liste blanche `SYSTEM_FILENAMES` et OPT-IN (cf. l'amendement d'ADR-0010).
 *
 * Ce que ce lot ajoute, et que le balayage global n'avait pas à traiter : le disque dit
 * si le répertoire est physiquement supprimable, mais **la BASE peut encore le
 * référencer**. Un dossier de l'onglet Dossiers est dérivé d'`assets.dir_path` : le
 * supprimer sur disque en laissant des lignes `assets` (typiquement des `is_missing`
 * dont le fichier a déjà disparu) le laisserait AFFICHÉ alors qu'il n'existe plus.
 * D'où le refus `indexed_assets` — conservateur, il ne détruit rien et renvoie vers les
 * écrans qui savent nettoyer l'index.
 */
// ---------------------------------------------------------------------------
// Nom de dossier (UN segment, pas un chemin)
// ---------------------------------------------------------------------------
/** Longueur maximale d'un NOM de dossier (un segment). */
export const FOLDER_NAME_MAX = 255;
/**
 * Normalise et valide un NOM de dossier — UN SEUL SEGMENT, jamais un chemin.
 * `null` = refusé.
 *
 * Distincte de `sanitizeDestFolder` (`organize.ts`), qui valide un CHEMIN relatif
 * multi-segments : ici, un `/` ou un `\` est une ERREUR et non un séparateur, parce
 * qu'un champ « Nom du dossier » qui accepterait `a/b` créerait DEUX répertoires sans
 * le dire. Distincte aussi de `sanitizeUploadFilename`, qui STRIPPE silencieusement les
 * composantes de chemin : silencieux est exactement ce qu'on ne veut pas d'un champ que
 * l'utilisateur relit avant de valider.
 *
 * Règles (→ `null` si l'une échoue) :
 *  - `trim`, refus si vide ou plus long que `FOLDER_NAME_MAX` ;
 *  - refus de `.` et `..` (segments de chemin), de tout caractère de contrôle
 *    (0x00–0x1F) et de tout séparateur `/` ou `\` ;
 *  - refus des noms EXCLUS DU LISTAGE (`isExcludedFolderName` : nom commençant par un
 *    point, `@eaDir`, `#recycle`…). Ce dernier point est le seul qui ne relève pas de la
 *    sûreté des chemins mais de la COHÉRENCE : l'arbre du lot 3 ne montre pas ces
 *    répertoires, donc les créer produirait un dossier qui n'apparaît nulle part —
 *    précisément le défaut que le lot 3 a corrigé.
 */
export function sanitizeFolderName(input) {
    const name = input.trim();
    if (name === '' || name.length > FOLDER_NAME_MAX)
        return null;
    if (name === '.' || name === '..')
        return null;
    if (name.includes('/') || name.includes('\\'))
        return null;
    for (let i = 0; i < name.length; i += 1) {
        if (name.charCodeAt(i) <= 0x1f)
            return null;
    }
    if (isExcludedFolderName(name))
        return null;
    return name;
}
// ---------------------------------------------------------------------------
// Chemins : parent / dernier segment / parenté
// ---------------------------------------------------------------------------
/** Chemin du PARENT d'un dossier relatif POSIX (`''` = racine). PURE. */
export function parentFolderPath(path) {
    const i = path.lastIndexOf('/');
    return i === -1 ? '' : path.slice(0, i);
}
/** Dernier segment d'un chemin relatif POSIX (le NOM du dossier). PURE. */
export function folderNameOf(path) {
    const i = path.lastIndexOf('/');
    return i === -1 ? path : path.slice(i + 1);
}
/**
 * `candidate` est-il `path` LUI-MÊME ou l'un de ses DESCENDANTS ? PURE.
 *
 * C'est la transposition JS EXACTE du prédicat SQL de sous-arbre du projet
 * (`rel_path = $1 OR rel_path LIKE like_escape($1) || '/%'`), y compris son piège :
 * `2024/vacances` ne couvre PAS `2024/vacances-bis`. Une comparaison naïve
 * `startsWith(path)` emporterait le second — c'est LA faute classique du renommage de
 * dossier, et la raison de la barre oblique dans le test.
 */
export function isSelfOrDescendantFolder(candidate, path) {
    return candidate === path || candidate.startsWith(`${path}/`);
}
// ---------------------------------------------------------------------------
// Requêtes
// ---------------------------------------------------------------------------
/**
 * Corps de `POST /api/libraries/:id/folders` — CRÉER un sous-dossier.
 * `parent` = chemin relatif du dossier hôte (`''` ou absent = racine) ; `name` = nom du
 * nouveau dossier (UN segment, validé par `sanitizeFolderName` côté route ET côté web).
 *
 * ⚠ AUCUN `.transform` ici, et il ne faut JAMAIS en ajouter : un `.transform` dans un
 * schéma de corps ou de query casse `z.toJSONSchema` au démarrage et met l'API en boucle
 * de redémarrage (régression réelle en v0.70.0). La normalisation se fait dans le
 * HANDLER. Garde : `route-json-schema.test.ts`.
 */
export const createFolderRequestSchema = z.object({
    parent: z.string().max(1024).optional(),
    name: z.string().min(1).max(FOLDER_NAME_MAX),
});
/**
 * Corps de `POST /api/libraries/:id/folders/rename` — RENOMMER un dossier.
 * `path` = dossier visé (jamais `''` : la racine ne se renomme pas) ; `name` = NOUVEAU
 * nom (un segment). Le parent est INCHANGÉ par construction — renommer et déplacer sont
 * deux gestes distincts dans l'UI, et deux routes distinctes ici.
 */
export const renameFolderRequestSchema = z.object({
    path: z.string().min(1).max(1024),
    name: z.string().min(1).max(FOLDER_NAME_MAX),
});
/**
 * Corps de `POST /api/libraries/:id/folders/move` — DÉPLACER un dossier.
 * `path` = dossier visé (jamais `''`) ; `destParent` = nouveau dossier HÔTE (`''` ou
 * absent = racine). Le nom du dossier est conservé.
 */
export const moveFolderRequestSchema = z.object({
    path: z.string().min(1).max(1024),
    destParent: z.string().max(1024).optional(),
});
/** Query de `GET /api/libraries/:id/folder-impact` — cf. `folderImpactSchema`. */
export const folderImpactQuerySchema = z.object({
    path: z.string().min(1).max(1024),
});
/**
 * Corps de `POST /api/libraries/:id/folders/delete` — SUPPRIMER un dossier et son
 * sous-arbre (XPHOTO-32, lot 5). `path` = le dossier visé (jamais `''`).
 *
 * `removeSystemFiles` (défaut **false**) est le MÊME opt-in que
 * `cleanEmptyFoldersRequestSchema`, avec la même portée : sans lui, un sous-arbre qui ne
 * contient qu'un `.DS_Store` n'est PAS supprimé — il est SIGNALÉ. Avec lui, et seulement
 * alors, les fichiers dont le nom EXACT figure dans `SYSTEM_FILENAMES` sont effacés
 * (`unlink`) avant les `rmdir`. Aucun autre fichier ne peut être effacé, jamais.
 */
export const deleteFolderRequestSchema = z.object({
    path: z.string().min(1).max(1024),
    removeSystemFiles: z.boolean().default(false),
});
/** Query de `GET /api/libraries/:id/folder-delete-preview` — cf. `folderDeletePreviewSchema`. */
export const folderDeletePreviewQuerySchema = z.object({
    path: z.string().min(1).max(1024),
});
// ---------------------------------------------------------------------------
// Réponses
// ---------------------------------------------------------------------------
/**
 * Résultat d'une écriture de dossier. `path` est le chemin FINAL (celui à ouvrir après
 * l'action) ; `previousPath` l'ancien (`null` à la création).
 *
 * Les trois compteurs disent CE QUI A ÉTÉ RECALÉ, et ils sont rendus délibérément :
 * c'est la seule preuve, côté appelant, que la propagation a bien eu lieu (« 312 médias,
 * 1 partage public, 2 accès »). Ils viennent tous de la MÊME transaction.
 */
export const folderOpResultSchema = z.object({
    path: z.string(),
    previousPath: z.string().nullable(),
    assetsUpdated: z.number().int().nonnegative(),
    filePathsUpdated: z.number().int().nonnegative(),
    sharesUpdated: z.number().int().nonnegative(),
    grantsUpdated: z.number().int().nonnegative(),
});
/**
 * IMPACT d'un renommage/déplacement, calculé AVANT de le faire
 * (`GET /api/libraries/:id/folder-impact?path=…`) : ce que la fenêtre de confirmation
 * doit annoncer.
 *
 * `assets` / `shares` / `grants` suivront automatiquement (propagation). `savedFilters`
 * est la seule ligne d'AVERTISSEMENT : ces filtres NE seront PAS réécrits (cf. l'en-tête
 * de ce module) et cesseront donc de désigner quoi que ce soit. Le compte porte sur
 * TOUTE la bibliothèque, pas seulement sur les filtres de l'appelant : un admin qui
 * range les dossiers casse les filtres du propriétaire, et lui cacher le nombre ne les
 * réparerait pas. Un COMPTE ne révèle ni le nom ni l'expression d'un filtre d'autrui.
 */
export const folderImpactSchema = z.object({
    path: z.string(),
    assets: z.number().int().nonnegative(),
    shares: z.number().int().nonnegative(),
    grants: z.number().int().nonnegative(),
    savedFilters: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// Suppression ciblée d'un dossier (lot 5)
// ---------------------------------------------------------------------------
/**
 * MOTIFS DE REFUS d'une suppression ciblée. Une liste VIDE = le sous-arbre est
 * supprimable (aux fichiers système près, qui relèvent de l'opt-in, pas d'un refus).
 *
 * Chacun appelle une conduite DIFFÉRENTE, d'où des valeurs distinctes plutôt qu'un
 * booléen : sortir les photos n'est pas nettoyer l'index, et un `.stfolder` ne se
 * traite pas comme une permission manquante.
 *
 *  - `indexed_assets` : des médias INDEXÉS référencent encore le sous-arbre — y compris
 *    des lignes `is_missing = true` dont le fichier a déjà disparu. C'est LE piège de ce
 *    lot : les dossiers de l'onglet Dossiers sont dérivés d'`assets.dir_path`, donc
 *    supprimer le répertoire en laissant ces lignes ferait SURVIVRE le dossier à
 *    l'écran alors qu'il n'existe plus sur disque. La sortie est de nettoyer l'index
 *    d'abord (console Admin : « Poubelle », « Fichiers absents »).
 *  - `other_files` : un fichier hors liste blanche (ou un lien symbolique, une socket…)
 *    est présent quelque part dans le sous-arbre. Le produit n'efface JAMAIS ça.
 *  - `hidden_dir` : un répertoire caché (`.` en tête, p. ex. `.stfolder` de Syncthing)
 *    est présent. Ces répertoires sont SANCTUARISÉS depuis l'ADR-0010 : ni parcourus,
 *    ni supprimés, et ils rendent leur parent non supprimable.
 *  - `unreadable` : un sous-répertoire n'a pas pu être inspecté. On ne supprime jamais
 *    ce qu'on n'a pas pu lire.
 */
export const FOLDER_DELETE_BLOCKERS = [
    'indexed_assets',
    'other_files',
    'hidden_dir',
    'unreadable',
];
export const folderDeleteBlockerSchema = z.enum(FOLDER_DELETE_BLOCKERS);
/** Longueur max de l'échantillon de noms de fichiers système rendu par l'aperçu. */
export const FOLDER_DELETE_SAMPLE_MAX = 20;
/**
 * APERÇU d'une suppression ciblée (`GET /api/libraries/:id/folder-delete-preview?path=…`),
 * en LECTURE seule : ce que la fenêtre de confirmation doit annoncer AVANT d'agir.
 *
 * ⚠ Un aperçu est PÉRISSABLE et n'est JAMAIS ce qui décide au moment d'agir — même
 * règle que `empty-folders.ts` : la cible est RE-CALCULÉE par la route de suppression.
 * Entre les deux, la bibliothèque a pu bouger (Syncthing, un téléversement, un autre
 * onglet), et c'est le second calcul qui fait foi.
 *
 *  - `folders` : nombre de répertoires qui seraient supprimés, le dossier visé COMPRIS.
 *  - `systemFiles` / `systemFileNames` : les fichiers de `SYSTEM_FILENAMES` trouvés dans
 *    le sous-arbre, comptés et NOMMÉS (noms distincts, bornés à
 *    `FOLDER_DELETE_SAMPLE_MAX`). Dire « 3 fichiers seront effacés » sans dire lesquels
 *    ne serait pas une confirmation.
 *  - `assets` : médias indexés référençant le sous-arbre (présents OU absents du disque).
 *  - `blockers` : vide ⇒ supprimable. Non vide ⇒ la suppression sera REFUSÉE, et
 *    l'utilisateur doit lire pourquoi avant de chercher le bouton.
 */
export const folderDeletePreviewSchema = z.object({
    path: z.string(),
    folders: z.number().int().nonnegative(),
    systemFiles: z.number().int().nonnegative(),
    systemFileNames: z.array(z.string()),
    assets: z.number().int().nonnegative(),
    blockers: z.array(folderDeleteBlockerSchema),
});
/**
 * Résultat d'une suppression ciblée. ÉTEND `folderOpResultSchema` — même contrat de
 * navigation que les trois autres gestes : `path` est le chemin à OUVRIR ensuite (ici le
 * dossier PARENT, puisque le dossier visé n'existe plus) et `previousPath` celui qui a
 * disparu. Rester sur un dossier supprimé serait une impasse.
 *
 * Les quatre compteurs de propagation valent TOUJOURS 0, et ce n'est pas un remplissage :
 * c'est la garantie du lot énoncée en chiffres. Une suppression est REFUSÉE dès qu'un
 * asset indexé référence le sous-arbre, donc il n'y a par construction rien à recaler —
 * ni média, ni chemin physique, ni partage, ni accès.
 *
 * `foldersRemoved` / `filesRemoved` sont les deux nombres propres à ce geste.
 * `filesRemoved` compte le SEUL `unlink` du produit (liste blanche, opt-in) : il vaut 0
 * sans `removeSystemFiles`.
 */
export const folderDeleteResultSchema = folderOpResultSchema.extend({
    foldersRemoved: z.number().int().nonnegative(),
    filesRemoved: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// Filtres enregistrés : détection d'un prédicat `folder:` visé
// ---------------------------------------------------------------------------
/**
 * L'AST d'un filtre enregistré contient-il un prédicat `folder:` visant `path` ou l'un
 * de ses descendants ? PURE, partagée front + back (le serveur compte, la SPA pourrait
 * expliquer). Parcours complet de l'arbre : un `folder:` peut être niché sous n'importe
 * quelle combinaison de `et`/`ou`/`non`/`sauf`.
 *
 * On compte aussi les DESCENDANTS parce qu'un renommage de `2024` casse tout autant un
 * filtre écrit sur `2024/vacances` — l'utilisateur qui lit « 2 filtres enregistrés
 * référencent ce dossier » veut le nombre de filtres qui vont cesser de fonctionner,
 * pas le nombre d'égalités exactes.
 */
export function filterAstReferencesFolder(node, path) {
    switch (node.kind) {
        case 'folder':
            return isSelfOrDescendantFolder(node.value, path);
        case 'and':
        case 'or':
            return node.children.some((child) => filterAstReferencesFolder(child, path));
        case 'not':
            return filterAstReferencesFolder(node.child, path);
        default:
            return false;
    }
}
//# sourceMappingURL=folder-ops.js.map